import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
// @ts-expect-error module not typed
import { getCanonicalVid } from 'conversations-internal-schema/visitor/operators/visitorGetters';
import { WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
import { getHasFileAttachment, getInboxId, getOriginalGenericChannelId, getPreview, getStatus, getThreadId, getVid, getVisitor } from 'conversations-thread-data/thread-details/public/operators';
// @ts-expect-error module not typed
import ThreadPresence from 'conversations-thread-view/presence/components/ThreadPresence';
import { useState } from 'react';
import styled from 'foundations-theming/utils';
import { CapabilitiesEditor } from '../../../capability-editor/public/components';
import { flexColumnStyles } from '../../../editor-utilities/protected/constants';
import { ToolbarExpandButton } from '../../../pop-out/public/components';
import { ComposerSettingsMenu } from '../../../settings/protected/components';
import { useDisplayCloseLiveChatButton } from '../hooks/useDisplayCloseLiveChatButton';
import { ChannelTabs } from './ChannelTabs';
import { CloseLiveChatButton } from './CloseLiveChatButton';
import { CommentTab } from './CommentTab';
import { TabbedEditorErrorState } from './TabbedEditorErrorState';
import { Flex } from 'foundations-components/components/Flex';
import { CustomTab } from './CustomTab';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { Tabs } from 'foundations-components/transitional/components/Tabs';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PositioningWrapper = styled.div.withConfig({
  displayName: "TabbedEditor__PositioningWrapper",
  componentId: "sc-1ccgafq-0"
})(["position:relative;flex-grow:1;flex-shrink:1;", " min-inline-size:100px;"], flexColumnStyles);
const TabButtonsGroup = styled.div.withConfig({
  displayName: "TabbedEditor__TabButtonsGroup",
  componentId: "sc-1ccgafq-1"
})(["flex-shrink:1;display:flex;align-items:center;"]);
const EmptyDisabledMessage = () => {
  return /*#__PURE__*/_jsx(Text, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer.empty-disabled-message"
    })
  });
};
EmptyDisabledMessage.displayName = 'EmptyDisabledMessage';
function getComponentDataForLogging(threadDetails) {
  return threadDetails ? {
    threadId: getThreadId(threadDetails),
    genericChannelId: getOriginalGenericChannelId(threadDetails),
    status: getStatus(threadDetails),
    inboxId: getInboxId(threadDetails),
    preview: getPreview(threadDetails),
    vid: getVid(threadDetails),
    hasFileAttachment: getHasFileAttachment(threadDetails)
  } : {};
}
const EditorTabBar = ({
  children
}) => /*#__PURE__*/_jsx(Flex, {
  flexBasis: "row",
  justifyContent: "space-between",
  children: /*#__PURE__*/_jsx(Flex, {
    flexGrow: "1",
    children: children
  })
});
EditorTabBar.displayName = 'EditorTabBar';
export function TabbedEditor({
  associatedObjectId,
  associatedObjectTypeId,
  asyncEmptyReply,
  callingSource,
  canSubmit = true,
  capabilitiesBlob,
  currentChannelId,
  disableCalling,
  disabled,
  disabledMessage,
  editorComponent,
  expandable = false,
  isKnownVisitor,
  isLoading,
  latestCMFGenericChannelId,
  onCloseShepherd,
  onDraftChange,
  onSelectedChange,
  originalGenericChannelId,
  renderExistingFileSelect,
  renderSubmitButton,
  replyRecommendationsEnabled,
  sendDisabledValidator,
  submitHandler,
  threadDetails,
  hideComments,
  CustomHeaderActions,
  defaultSendFromEnabled,
  customEditorViews,
  macrosEnabled
}) {
  var _customEditorViews$vi;
  const displayCloseLiveChatButton = useDisplayCloseLiveChatButton({
    currentChannelId
  });
  const defaultSelectedTab = 'channel';
  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab);
  const handleSelectedChange = (tabId, channelId) => {
    // This will be the id of the tab, and the dropdown tab will have one id for all dropdown options
    setSelectedTab(tabId);
    // This will be the id of the channel, and the dropdown tab will have one id for each dropdown option, otherwise undefined if the dropdown tab was not selected
    onSelectedChange(channelId !== null && channelId !== void 0 ? channelId : tabId);
  };
  if (disabled) {
    if (!disabledMessage) {
      Raven.captureException(new Error('TabbedEditor is disabled but no disabledMessage was provided.'));
      return /*#__PURE__*/_jsx(EmptyDisabledMessage, {});
    }
    return /*#__PURE__*/_jsx(View, {
      padding: "400",
      textAlign: "center",
      children: disabledMessage
    });
  }
  const visitor = getVisitor(threadDetails);
  const subjectId = getCanonicalVid(visitor);
  const inboxId = getInboxId(threadDetails);
  const threadId = getThreadId(threadDetails);
  const eligibleChannel = currentChannelId !== WHATSAPP_GENERIC_CHANNEL_ID;
  const displayComposerSettings = eligibleChannel && replyRecommendationsEnabled;
  const CustomEditorComponent = customEditorViews === null || customEditorViews === void 0 || (_customEditorViews$vi = customEditorViews.views.find(view => view.key === currentChannelId)) === null || _customEditorViews$vi === void 0 ? void 0 : _customEditorViews$vi.render;
  const TabbedComponent = CustomEditorComponent || editorComponent || CapabilitiesEditor;
  const shouldRenderTabbedComponent = CustomEditorComponent ? true : !isLoading && capabilitiesBlob;
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    renderError: TabbedEditorErrorState,
    errorLoggingTitle: "An uncaught TabbedChannels error occurred",
    errorBoundaryName: "TabbedChannelsBoundary",
    onGetComponentDataForLogging: () => getComponentDataForLogging(threadDetails),
    children: /*#__PURE__*/_jsxs(PositioningWrapper, {
      "data-test-id": "TabbedEditorPositionWrapper",
      children: [isLoading || !originalGenericChannelId ? /*#__PURE__*/_jsx(LoadingOverlay, {
        use: "dots",
        contextual: true
      }) : /*#__PURE__*/_jsxs(Flex, {
        backgroundColor: "fill-surface-default",
        borderBlockStartWidth: "100",
        borderBlockStartColor: "border-core-default",
        borderBlockStartStyle: "solid",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Tabs, {
          Wrapper: EditorTabBar,
          defaultSelected: defaultSelectedTab,
          selected: selectedTab,
          onSelectedChange: ({
            target: {
              value: tabId
            }
          }) => {
            // When the channel tab is selected, onSelectedDropdownChange handles logic instead
            if (tabId !== 'channel') {
              handleSelectedChange(String(tabId));
            }
          },
          children: [/*#__PURE__*/_jsx(ChannelTabs, {
            tabId: "channel",
            associatedObjectId: associatedObjectId,
            associatedObjectTypeId: associatedObjectTypeId,
            currentChannelId: currentChannelId,
            callingSource: callingSource,
            disableCalling: disableCalling,
            isKnownVisitor: isKnownVisitor,
            onSelectedDropdownChange: channelId => {
              handleSelectedChange('channel', String(channelId));
            },
            originalGenericChannelId: originalGenericChannelId,
            subjectId: subjectId,
            inboxId: inboxId,
            threadId: `${threadId}`
          }), !hideComments && /*#__PURE__*/_jsx(CommentTab, {
            tabId: "comment"
          }), customEditorViews && customEditorViews.views.map(view => /*#__PURE__*/_jsx(CustomTab, {
            tabId: view.fragment,
            channelKey: view.key,
            tabLabel: view.label
          }, `custom-tab-${view.key}`))]
        }), /*#__PURE__*/_jsxs(TabButtonsGroup, {
          children: [CustomHeaderActions ? /*#__PURE__*/_jsx(Flex, {
            display: "inline-flex",
            children: CustomHeaderActions
          }) : null, /*#__PURE__*/_jsx(ThreadPresence, {
            threadId: threadId
          }), displayComposerSettings && /*#__PURE__*/_jsx(ComposerSettingsMenu, {}), displayCloseLiveChatButton ? /*#__PURE__*/_jsx(CloseLiveChatButton, {
            onSelectedChange: onSelectedChange
          }) : expandable ? /*#__PURE__*/_jsx(ToolbarExpandButton, {
            editorKey: "replyEditor"
          }) : null]
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexGrow: 1,
        flexDirection: "column",
        alignItems: "stretch",
        backgroundColor: "fill-surface-default",
        borderBlockStartWidth: "100",
        borderBlockStartColor: "border-core-default",
        borderBlockStartStyle: "solid",
        children: shouldRenderTabbedComponent && /*#__PURE__*/_jsx(TabbedComponent, {
          asyncEmptyReply: asyncEmptyReply,
          canSubmit: canSubmit,
          capabilitiesBlob: capabilitiesBlob,
          currentChannelId: currentChannelId,
          latestCMFGenericChannelId: latestCMFGenericChannelId,
          onCloseShepherd: onCloseShepherd,
          onDraftChange: onDraftChange,
          originalGenericChannelId: originalGenericChannelId || null,
          renderExistingFileSelect: renderExistingFileSelect,
          replyRecommendationsEnabled: replyRecommendationsEnabled,
          renderSubmitButton: renderSubmitButton,
          sendDisabledValidator: sendDisabledValidator,
          submitHandler: submitHandler,
          threadDetails: threadDetails,
          defaultSendFromEnabled: defaultSendFromEnabled,
          macrosEnabled: macrosEnabled
        })
      })]
    })
  });
}
TabbedEditor.displayName = '';